import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Loader from "components/DesignSystem/Loader/loader";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { DISPLAY_DATE } from "constants/date";
import {
  CREDIT_NOTE_STATUS_LABEL,
  CREDIT_NOTE_STATUS_TAG_TYPE,
} from "constants/creditNotes";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useLocation } from "react-router-dom";
import { useGetCreditNoteQuery } from "store/apis/creditNotes";
import { pluralize } from "utils/pluralize";

type CreditNoteSliderProps = {
  creditNoteId: string;
  customerId: string;
};

export const CreditNoteSlider = ({
  creditNoteId,
  customerId,
}: CreditNoteSliderProps) => {
  const { search } = useLocation();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data: creditNote, isLoading } = useGetCreditNoteQuery(
    {
      groupId,
      entityId: entityId!,
      credit_debit_note_id: creditNoteId,
      customerId,
    },
    { skip: !groupId || !entityId || !creditNoteId || !customerId }
  );

  const linkedInvoices = creditNote?.linked_invoices || [];

  if (isLoading) {
    return (
      <div className="t-flex t-justify-center t-items-center t-h-full">
        <Loader />
      </div>
    );
  }

  if (!creditNote) {
    return null;
  }

  return (
    <>
      <Slider.Header>
        <Slider.Title className="align-center t-text-subtitle t-flex t-items-center t-gap-2">
          {creditNote.note_id}
          <span className="t-flex t-text-body-sm">
            <Tag
              rounded
              size="small"
              tagType={CREDIT_NOTE_STATUS_TAG_TYPE[creditNote.status]}
              icon={false}
            >
              {CREDIT_NOTE_STATUS_LABEL[creditNote.status]}
            </Tag>
          </span>
        </Slider.Title>
        <Slider.Close />
      </Slider.Header>

      <Slider.Body className="t-flex t-flex-col t-gap-4">
        <Accordion.Root
          type="multiple"
          defaultValue={["DETAILS", "LINKED_INVOICES"]}
          className="t-flex t-flex-col t-gap-4"
        >
          <Accordion.Item value="DETAILS">
            <Accordion.Trigger>Details</Accordion.Trigger>
            <Accordion.Content className="t-flex t-flex-col t-gap-6">
              <div className="t-grid t-grid-cols-2">
                <Accordion.ItemGrid>
                  <div className="t-col-span-2">
                    <InfoItem label="Creation date">
                      {creditNote?.date || "-"}
                    </InfoItem>
                  </div>
                </Accordion.ItemGrid>
                <Accordion.ItemGrid>
                  <div className="t-col-span-2">
                    <InfoItem label="Credit amount">
                      <AmountSuperScript
                        amount={Number(creditNote.amount) || 0}
                      />
                    </InfoItem>
                  </div>
                </Accordion.ItemGrid>
              </div>
              <div className="t-grid t-grid-cols-2">
                <Accordion.ItemGrid>
                  <div className="t-col-span-2">
                    <InfoItem label="Description">
                      {creditNote?.description || "-"}
                    </InfoItem>
                  </div>
                </Accordion.ItemGrid>
              </div>
            </Accordion.Content>
          </Accordion.Item>

          {linkedInvoices.length > 0 && (
            <Accordion.Item value="LINKED_INVOICES">
              <Accordion.Trigger>
                Linked to{" "}
                {pluralize(linkedInvoices.length, " Invoice", " Invoices")}
              </Accordion.Trigger>
              <Accordion.Content className="t-flex t-justify-center">
                <div className="t-w-full t-max-w-full">
                  <div className="t-grid t-grid-cols-3 t-gap-2 t-py-2 t-border-0 t-border-b t-border-solid t-border-neutral-10">
                    <div className="t-text-body-sm t-text-text-30">
                      Invoice date
                    </div>
                    <div className="t-text-body-sm t-text-text-30">
                      Invoice no.
                    </div>
                    <div className="t-text-body-sm t-text-text-30">Amount</div>
                  </div>
                  {linkedInvoices.map((invoice) => (
                    <div
                      key={invoice.uuid}
                      className="t-grid t-grid-cols-3 t-gap-2 t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-0"
                    >
                      <div className="t-text-body-sm t-text-text-100">
                        {invoice.invoice_date
                          ? dayjs(invoice.invoice_date).format(DISPLAY_DATE)
                          : "-"}
                      </div>
                      <div className="t-text-body-sm">
                        <Link
                          to={`/books/invoices-and-customers/${invoice.uuid}${search}`}
                        >
                          {invoice.invoice_number}
                        </Link>
                      </div>
                      <div className="t-text-body-sm t-text-text-100">
                        {invoice.total ? (
                          <AmountSuperScript amount={invoice.total} />
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Accordion.Content>
            </Accordion.Item>
          )}
        </Accordion.Root>
      </Slider.Body>
    </>
  );
};
